import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  asignarUsuarioProyecto,
  buscarUsuariosDisponibles,
} from "../usuariosProyectosApi";
import {
  asignarUsuariosSecuencial,
  getProyectoId,
  getProyectoLabel,
  getUsuarioId,
  getUsuarioNombre,
  getUsuariosProyectosErrorMessage,
} from "../usuariosProyectosUtils";

const AsignarUsuariosDialog = ({ open, project, onClose, onAssigned }) => {
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [result, setResult] = useState(null);
  const searchSequence = useRef(0);
  const projectId = getProyectoId(project);

  useEffect(() => {
    if (!open || !projectId) {
      searchSequence.current += 1;
      setSearch("");
      setOptions([]);
      setSelected([]);
      setSearchError("");
      setResult(null);
      return undefined;
    }

    const sequence = ++searchSequence.current;
    const timer = setTimeout(async () => {
      setIsSearching(true);
      setSearchError("");
      try {
        const users = await buscarUsuariosDisponibles(projectId, search, 50);
        if (sequence === searchSequence.current) setOptions(users);
      } catch (error) {
        if (sequence === searchSequence.current) {
          setOptions([]);
          setSearchError(getUsuariosProyectosErrorMessage(error, "search"));
        }
      } finally {
        if (sequence === searchSequence.current) setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [open, projectId, search]);

  const autocompleteOptions = useMemo(() => {
    const users = new Map();
    [...selected, ...options].forEach((user) => {
      users.set(String(getUsuarioId(user)), user);
    });
    return [...users.values()];
  }, [options, selected]);

  const handleAssign = async () => {
    if (!projectId || selected.length === 0) return;
    setIsAssigning(true);
    setResult(null);

    const assignmentResult = await asignarUsuariosSecuencial({
      idProyecto: projectId,
      usuarios: selected,
      assign: asignarUsuarioProyecto,
    });

    setIsAssigning(false);
    setSelected(assignmentResult.failures.map(({ user }) => user));
    setResult(assignmentResult);
    await onAssigned?.(assignmentResult);

    if (assignmentResult.failures.length === 0) onClose?.();
  };

  return (
    <Dialog open={open} onClose={isAssigning ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Asignar usuarios</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Proyecto: <strong>{getProyectoLabel(project)}</strong>
        </Typography>
        <TextField
          label="Buscar usuario"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Nombre, correo, rol o cargo"
          fullWidth
          size="small"
          sx={{ mb: 2 }}
        />
        <Autocomplete
          multiple
          options={autocompleteOptions}
          value={selected}
          onChange={(event, newValue) => {
            setSelected(newValue);
            setResult(null);
          }}
          filterOptions={(items) => items}
          getOptionLabel={getUsuarioNombre}
          isOptionEqualToValue={(option, value) =>
            String(getUsuarioId(option)) === String(getUsuarioId(value))
          }
          loading={isSearching}
          noOptionsText={searchError || "No hay usuarios disponibles"}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Usuarios disponibles"
              placeholder="Selecciona uno o varios usuarios"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isSearching && <CircularProgress color="inherit" size={18} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={getUsuarioId(option)}>
              <Stack spacing={0.25}>
                <Typography variant="body2" fontWeight={700}>{getUsuarioNombre(option)}</Typography>
                {option.nombreRol && <Typography variant="caption">{option.nombreRol}</Typography>}
                {option.email && <Typography variant="caption" color="text.secondary">{option.email}</Typography>}
                {(option.cargo || option.ocupacion) && (
                  <Typography variant="caption" color="text.secondary">
                    {option.cargo || option.ocupacion}
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
        />
        {searchError && <Alert severity="error" sx={{ mt: 2 }}>{searchError}</Alert>}
        {result && (
          <Alert severity={result.failures.length ? "warning" : "success"} sx={{ mt: 2 }}>
            <Typography variant="body2">
              {result.successes.length} usuario{result.successes.length === 1 ? "" : "s"} asignado{result.successes.length === 1 ? "" : "s"} correctamente.
            </Typography>
            {result.failures.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  {result.failures.length} usuario{result.failures.length === 1 ? "" : "s"} no pudo{result.failures.length === 1 ? "" : "ieron"} asignarse.
                </Typography>
                {result.failures.map(({ user, message }) => (
                  <Typography key={getUsuarioId(user)} variant="caption" display="block">
                    {getUsuarioNombre(user)}: {message}
                  </Typography>
                ))}
              </Box>
            )}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isAssigning}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={isAssigning || selected.length === 0}
        >
          {isAssigning ? "Asignando..." : "Asignar seleccionados"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AsignarUsuariosDialog;
